export const CATEGORY_FILTER_GROUP_IDS = ['academic_calendar', 'events', 'reviews'];
export const FLAT_FILTER_GROUP_IDS = [...CATEGORY_FILTER_GROUP_IDS, 'audiences'];
export const AFFILIATION_FILTER_GROUP_IDS = [
  'event_affiliations',
  'arch_prog_affiliations',
  'larch_prog_affiliations',
  'upd_prog_affiliations',
  'other_prog_affiliations',
  'office_affiliations',
  'student_affiliations',
];

export function allFilterSelection() {
  return { all: true, values: [] };
}

export function emptyFilterSelection() {
  return { all: false, values: [] };
}

export function allAffiliationSelection() {
  return { all: true, values: emptyAffiliationValues() };
}

export function emptyAffiliationSelection() {
  return { all: false, values: emptyAffiliationValues() };
}

export function emptyAffiliationValues() {
  return Object.fromEntries(AFFILIATION_FILTER_GROUP_IDS.map(group => [group, []]));
}

export function isAffiliationFilterGroup(groupId) {
  return groupId === 'affiliations';
}

export function allSelectionForGroup(groupId) {
  if (isAffiliationFilterGroup(groupId)) {
    return allAffiliationSelection();
  }

  return allFilterSelection();
}

export function toggleAllSelection(selection, groupId) {
  if (!isSelectionAll(selection)) {
    return allSelectionForGroup(groupId);
  }

  return specificSelectionOrNone(selection, groupId);
}

export function createDefaultCalendarFilters() {
  return {
    academic_calendar: allFilterSelection(),
    events: allFilterSelection(),
    reviews: allFilterSelection(),
    audiences: allFilterSelection(),
    affiliations: allAffiliationSelection(),
  };
}

export function createAcademicCalendarDefaultFilters() {
  return {
    academic_calendar: allFilterSelection(),
    events: emptyFilterSelection(),
    reviews: emptyFilterSelection(),
    audiences: allFilterSelection(),
    affiliations: allAffiliationSelection(),
  };
}

export function normalizeCalendarFilters(filters = {}, defaults = createDefaultCalendarFilters()) {
  const normalized = {};

  for (const group of FLAT_FILTER_GROUP_IDS) {
    normalized[group] = normalizeFlatSelection(filters?.[group], defaults[group]);
  }

  normalized.affiliations = normalizeAffiliationSelection(
    filters?.affiliations,
    defaults.affiliations
  );

  return normalized;
}

export function calendarFiltersForApi(filters) {
  const normalized = normalizeCalendarFilters(filters);
  const payload = {};

  for (const group of CATEGORY_FILTER_GROUP_IDS) {
    const isAll = isSelectionAll(normalized[group]);

    payload[`all_${group}`] = isAll;
    payload[group] = isAll ? [] : flatSelectionValues(normalized[group]);
  }

  const allAudiences = isSelectionAll(normalized.audiences);
  const allAffiliations = isSelectionAll(normalized.affiliations);

  payload.all_audiences = allAudiences;
  payload.audiences = allAudiences ? [] : flatSelectionValues(normalized.audiences);
  payload.all_affiliations = allAffiliations;
  payload.affiliations = allAffiliations
    ? emptyAffiliationValues()
    : affiliationValues(normalized.affiliations);

  return payload;
}

export function isSelectionAll(selection) {
  if (Array.isArray(selection)) {
    return selection.includes('all');
  }

  return selection?.all === true;
}

export function flatSelectionValues(selection) {
  if (Array.isArray(selection)) {
    return uniqueValues(selection.filter(value => value !== 'all'));
  }

  return uniqueValues(Array.isArray(selection?.values) ? selection.values : []);
}

export function affiliationValues(selection) {
  if (Array.isArray(selection)) {
    return {
      ...emptyAffiliationValues(),
      event_affiliations: uniqueValues(selection.filter(value => value !== 'all')),
    };
  }

  return normalizeAffiliationValues(selection?.values);
}

export function toggleFlatFilterValue(selection, value) {
  const id = filterOptionId(value);
  const values = flatSelectionValues(selection);
  const nextValues = values.includes(id)
    ? values.filter(selectedValue => selectedValue !== id)
    : [...values, id];

  return nextValues.length > 0 ? { all: false, values: nextValues } : allFilterSelection();
}

export function toggleAffiliationFilterValue(selection, option, children = []) {
  const id = filterOptionId(option);
  const field = filterOptionField(option);
  const values = cloneAffiliationValues(affiliationValues(selection));
  const groupValues = values[field] || [];
  const isSelecting = !groupValues.includes(id);

  values[field] = isSelecting
    ? [...groupValues, id]
    : groupValues.filter(selectedValue => selectedValue !== id);

  for (const child of children) {
    const childField = filterOptionField(child);
    const childId = filterOptionId(child);
    const childValues = values[childField] || [];

    values[childField] = isSelecting
      ? [...new Set([...childValues, childId])]
      : childValues.filter(selectedValue => selectedValue !== childId);
  }

  return hasSelectedAffiliationValues(values) ? { all: false, values } : allAffiliationSelection();
}

export function removeFilterValue(selection, itemId, field, groupId) {
  if (itemId === 'all') {
    return isAffiliationFilterGroup(groupId) ? emptyAffiliationSelection() : emptyFilterSelection();
  }

  if (isAffiliationFilterGroup(groupId)) {
    const values = cloneAffiliationValues(affiliationValues(selection));
    const affiliationField = field || 'event_affiliations';

    values[affiliationField] = (values[affiliationField] || []).filter(value => value !== itemId);

    return hasSelectedAffiliationValues(values)
      ? { all: false, values }
      : allAffiliationSelection();
  }

  const values = flatSelectionValues(selection).filter(value => value !== itemId);

  return values.length > 0 ? { all: false, values } : allFilterSelection();
}

export function filterOptionId(option) {
  return typeof option === 'string' ? option : option.id;
}

export function filterOptionField(option) {
  return typeof option === 'string' ? 'event_affiliations' : option.field || 'event_affiliations';
}

export function normalizeAffiliationValues(values) {
  if (Array.isArray(values)) {
    return {
      ...emptyAffiliationValues(),
      event_affiliations: uniqueValues(values.filter(value => value !== 'all')),
    };
  }

  const normalized = emptyAffiliationValues();

  if (!values || typeof values !== 'object') {
    return normalized;
  }

  for (const group of AFFILIATION_FILTER_GROUP_IDS) {
    normalized[group] = uniqueValues(Array.isArray(values[group]) ? values[group] : []);
  }

  return normalized;
}

function normalizeFlatSelection(value, fallback = allFilterSelection()) {
  if (value === undefined || value === null) {
    return cloneFlatSelection(fallback);
  }

  if (Array.isArray(value)) {
    return value.includes('all')
      ? allFilterSelection()
      : { all: false, values: uniqueValues(value) };
  }

  if (typeof value === 'object') {
    return {
      all: value.all === true,
      values: uniqueValues(Array.isArray(value.values) ? value.values : []),
    };
  }

  return cloneFlatSelection(fallback);
}

function normalizeAffiliationSelection(value, fallback = allAffiliationSelection()) {
  if (value === undefined || value === null) {
    return cloneAffiliationSelection(fallback);
  }

  if (Array.isArray(value)) {
    return value.includes('all')
      ? allAffiliationSelection()
      : { all: false, values: normalizeAffiliationValues(value) };
  }

  if (typeof value === 'object') {
    return {
      all: value.all === true,
      values: normalizeAffiliationValues(value.values || value),
    };
  }

  return cloneAffiliationSelection(fallback);
}

function cloneFlatSelection(selection = allFilterSelection()) {
  return {
    all: selection.all === true,
    values: uniqueValues(Array.isArray(selection.values) ? selection.values : []),
  };
}

function cloneAffiliationSelection(selection = allAffiliationSelection()) {
  return {
    all: selection.all === true,
    values: normalizeAffiliationValues(selection.values),
  };
}

function cloneAffiliationValues(values) {
  return {
    ...emptyAffiliationValues(),
    ...Object.fromEntries(Object.entries(values).map(([key, group]) => [key, [...group]])),
  };
}

function hasSelectedAffiliationValues(values) {
  return Object.values(values).some(group => group.length > 0);
}

function specificSelectionOrNone(selection, groupId) {
  if (isAffiliationFilterGroup(groupId)) {
    const values = affiliationValues(selection);

    return hasSelectedAffiliationValues(values)
      ? { all: false, values }
      : emptyAffiliationSelection();
  }

  const values = flatSelectionValues(selection);

  return values.length ? { all: false, values } : emptyFilterSelection();
}

function uniqueValues(values) {
  return [...new Set(values.map(value => String(value).trim()).filter(value => value !== ''))];
}
